import * as React from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { Check } from 'lucide-react'
import { cn } from '../../lib/utils'

export type CheckboxProps = React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
  /**
   * Optional label rendered next to the checkbox. When provided, the label
   * shares the same focus + click target as the box itself.
   */
  label?: React.ReactNode
  /**
   * Optional supporting text shown beneath the label in a smaller, muted
   * style. Useful for "Trust this device for 7 days"–style sub-copy.
   */
  description?: React.ReactNode
}

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, label, description, id, ...props }, ref) => {
  const reactId = React.useId()
  const inputId = id ?? reactId

  const box = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={inputId}
      className={cn(
        'peer h-4 w-4 shrink-0 rounded border border-slate-300 bg-white',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-slate-900 data-[state=checked]:text-white data-[state=checked]:border-slate-900',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        <Check className="h-3 w-3" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )

  if (!label && !description) return box

  return (
    <label
      htmlFor={inputId}
      className="flex items-start gap-2 cursor-pointer select-none text-sm"
    >
      {box}
      <div className="flex flex-col leading-tight">
        {label ? <span className="text-slate-900">{label}</span> : null}
        {description ? (
          <span className="text-xs text-slate-500 mt-0.5">{description}</span>
        ) : null}
      </div>
    </label>
  )
})

Checkbox.displayName = 'Checkbox'
